import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, purgeDecomForUser } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import GeneralDialog from '../../../../shared/general-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface PurgeUserDecomDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
}

export default function PurgeUserDecomDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
}: PurgeUserDecomDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => purgeDecomForUser(userEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User has been purged from decom!',
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DecomStatus, userEmail],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SuspendedUsers],
      });
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue purging the user from decom. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.purge_from_decom, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.userDetails,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.PurgeDecomForUser.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title={'Purge User Decom'}
      dialogContent={
        <Box sx={{ px: 4 }}>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Do you wish to purge {userEmail} from decom? This will stop the
            decom processing for the selected user if they are processing,
            remove them from decom, and delete all logs of the operations
            performed on them in decom.
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Update',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
